import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, HelpCircle, Sparkles } from "lucide-react";

const faqs = [
  {
    question: "What industries does Astergo Information Technology Pvt Limited serve?",
    answer:
      "At Astergo, we are committed to serving various industries, including healthcare, ride-sharing, e-commerce, gaming, fitness, and more. Here, you can access complete digital transformation from experts to lead long-term success and growth.",
    category: "Services"
  },
  {
    question: "What makes Astergo different from other agencies?",
    answer:
      "We deliver end-to-end services with in-house teams, custom strategies, and a focus on innovation and scalability. Our commitment to cutting-edge technology and client success sets us apart in the industry.",
    category: "Company"
  },
  {
    question: "How do you ensure project quality?",
    answer:
      "We follow a strict QA process with detailed testing, feedback loops, and dedicated project managers to ensure quality. Our multi-stage review process includes code reviews, automated testing, and client validation at every milestone.",
    category: "Process"
  },
  {
    question: "What kind of support do you offer after project completion?",
    answer:
      "We provide comprehensive post-project support including maintenance, updates, technical assistance, and ongoing optimization tailored to your needs. Our support team is available 24/7 to ensure your systems run smoothly.",
    category: "Support"
  },
  {
    question: "How long does a typical project take?",
    answer:
      "Project timelines vary based on complexity and scope. A simple mobile app may take 8-12 weeks, while complex enterprise solutions can take 4-6 months. We provide detailed timelines during our initial consultation.",
    category: "Process"
  },
  {
    question: "Do you provide custom software development?",
    answer:
      "Yes! We specialize in custom software development tailored to your specific business needs. From web applications to mobile apps and enterprise solutions, we build exactly what you need.",
    category: "Services"
  },
];

const FaqSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const toggleIndex = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="relative w-full bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 py-12 px-4 overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 right-0 w-72 h-72 bg-gradient-to-br from-blue-100/40 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-gradient-to-tr from-indigo-100/40 to-transparent rounded-full blur-3xl"></div>
      
      <div className="max-w-4xl mx-auto relative z-10">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {/* Badge */}
          <motion.div
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 rounded-full mb-4"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <HelpCircle className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-xs font-semibold text-blue-700">FAQ</span>
          </motion.div>

          {/* Heading */}
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
            Frequently Asked
            <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mt-1">
              Questions
            </span>
          </h2>
          
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Everything you need to know about our services and how we can help transform your business
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-3">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
              >
                <div
                  className={`group relative rounded-xl border-2 transition-all duration-300 ${
                    isOpen
                      ? "bg-white border-blue-500 shadow-xl shadow-blue-100/50"
                      : "bg-white/80 backdrop-blur-sm border-gray-200 hover:border-blue-300 hover:shadow-lg"
                  }`}
                >
                  {/* Category Badge */}
                  <div className="absolute -top-2 left-5 px-2.5 py-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-[10px] font-semibold rounded-full shadow-md">
                    {faq.category}
                  </div>

                  {/* Question Button */}
                  <button
                    onClick={() => toggleIndex(index)}
                    className="w-full flex justify-between items-start gap-3 px-5 pt-6 pb-4 text-left"
                  >
                    <div className="flex-1">
                      <h3 className={`text-base font-semibold transition-colors duration-300 ${
                        isOpen ? "text-blue-600" : "text-gray-900 group-hover:text-blue-600"
                      }`}>
                        {faq.question}
                      </h3>
                    </div>
                    
                    <motion.div
                      animate={{ rotate: isOpen ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                      className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isOpen
                          ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-200"
                          : "bg-gray-100 text-gray-600 group-hover:bg-blue-50 group-hover:text-blue-600"
                      }`}
                    >
                      {isOpen ? (
                        <Minus className="w-4 h-4" />
                      ) : (
                        <Plus className="w-4 h-4" />
                      )}
                    </motion.div>
                  </button>

                  {/* Answer with Animation */}
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 pb-4">
                          <div className="pt-1 border-t border-gray-100">
                            <p className="text-gray-700 text-sm leading-relaxed mt-3">
                              {faq.answer}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Hover Effect Border */}
                  <div className={`absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/0 via-blue-500/5 to-indigo-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none ${
                    isOpen ? "opacity-100" : ""
                  }`}></div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Still Have Questions CTA */}
        <motion.div
          className="mt-10 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          <div className="inline-block p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 shadow-lg">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <h3 className="text-lg font-bold text-gray-900">Still have questions?</h3>
            </div>
            <p className="text-gray-600 text-sm mb-4 max-w-md">
              Can't find the answer you're looking for? Please chat with our friendly team.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-semibold rounded-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              <span>Get in Touch</span>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FaqSection;